import React, { useEffect, useRef, useState } from "react";
import { Autocomplete, Box, CircularProgress, InputLabel, TextField, Typography, useTheme, Select, MenuItem } from "@mui/material";
import SubHeader from "../../../../components/SubHeader";
import TravelallowancesList from "./TravelallowancesList";
import styles from "./style";
import Modal from "../../../../components/Modal";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";

const TravelallowancesPage = ({
  data,
  getApprovedTravelAllowance,
  getLeaveManagementLists,
  handleLeaveApply,
  role,
  permission,
  handleFilter,
  handleSort,
  loading,
  employeeData,
  handleEmployee,
  open,
  setOpen,
  page,
  total,
  setPage,
  empData,
  DepartmentPayroll,
  departmentData,
  DeleteCommission,
  updateTravelAllowances,
  handleRequest,
  singleTravelClaimData,
  getSingleTravelClaimData,
  fetchTravelAllowanceDefaults,
}) => {
  const theme = useTheme();
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [department, setDepartment] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [employeeEmailSelected, setEmployeeEmailSelected] = useState(false);
  const [travelType, setTravelType] = useState("Allowance");
  const [kilometers, setKilometers] = useState("");
  const [ratePerKm, setRatePerKm] = useState("");
  const [taxablePercentage, setTaxablePercentage] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [getId, setGetId] = useState(null);
  const [isEdit, setIsEdit] = useState(null);
  const [empDataId, setEmpDataId] = useState(employeeData);
  const [description, setDescription] = useState("");
  const [selectedOptions, setSelectedOptions] = useState(data?.employeeDetails || []);
  const [originalData, setOriginalData] = useState(null);
  const [allowanceStatus, setAllowanceStatus] = useState(null);
  const defaultsRequestKeyRef = useRef("");
  const isEmployeeRole = role === "Employee" || role === "employee";

  useEffect(() => {
    const shouldDisableDepartment = (selectedOptions?.length || 0) > 0 && !department;
    if (employeeEmailSelected !== shouldDisableDepartment) {
      setEmployeeEmailSelected(shouldDisableDepartment);
    }
  }, [selectedOptions, department, employeeEmailSelected]);

  useEffect(() => {
    if (!department && singleTravelClaimData) {
      const rateValue = singleTravelClaimData?.ratePerKm;
      const taxableValue = singleTravelClaimData?.taxablePercentage;
      setRatePerKm(rateValue != null ? String(rateValue) : "");
      setTaxablePercentage(taxableValue != null ? String(taxableValue) : "");
    }
  }, [singleTravelClaimData, department]);

  const isCreateFormValid = () => {
    const hasEmployeeSelection = selectedOptions && selectedOptions.length > 0;
    if (
      !hasEmployeeSelection ||
      !fromDate ||
      !toDate ||
      !travelType ||
      !kilometers ||
      (!department && ratePerKm === "") ||
      (travelType === "Allowance" && taxablePercentage === "")
    ) {
      return false;
    }

    const numericKilometers = Number(kilometers);
    const numericRate = Number(ratePerKm);
    const numericTaxable = Number(taxablePercentage || 0);

    if (Number.isNaN(numericKilometers) || numericKilometers <= 0) return false;
    if (!department) {
      if (ratePerKm === "" || Number.isNaN(numericRate)) return false;
      if (numericRate < 0) return false;
    }
    if (travelType === "Allowance") {
      if (Number.isNaN(numericTaxable) || numericTaxable < 0 || numericTaxable > 100) return false;
    }

    return true;
  };

  const hasFormChanges = () => {
    if (!originalData || isEdit !== "edit") return true; // Allow save in non-edit mode or if no original data

    const kmChanged = String(kilometers || "") !== String(originalData.kilometer || "");
    const rateChanged = String(ratePerKm || "") !== String(originalData.ratePerKm || "");
    const taxableChanged = String(taxablePercentage || "") !== String(originalData.taxablePercentage || "");
    const startChanged = fromDate && originalData.start ? !dayjs(fromDate).isSame(dayjs(originalData.start), "day") : fromDate !== originalData.start;
    const endChanged = toDate && originalData.end ? !dayjs(toDate).isSame(dayjs(originalData.end), "day") : toDate !== originalData.end;
    const descChanged = String(description || "") !== String(originalData.description || "");
    const deptChanged = String(department || "") !== String(originalData.department || "");
    const typeChanged = String(travelType || "") !== String(originalData.travelType || "");
    const employeesChanged =
      JSON.stringify((selectedOptions || []).map((e) => e?._id).sort()) !==
      JSON.stringify((originalData.employeeDetails || []).map((e) => e?._id).sort());

    return (
      kmChanged ||
      rateChanged ||
      taxableChanged ||
      startChanged ||
      endChanged ||
      descChanged ||
      deptChanged ||
      typeChanged ||
      employeesChanged
    );
  };

  const isApplyButtonDisabled = () => {
    if (isEdit === "edit") {
      // Disable if no changes or form is invalid
      return !hasFormChanges() || !isCreateFormValid();
    }
    return !isCreateFormValid();
  };

  useEffect(() => {
    if (isEdit === "edit" && getId) {
      const hasDepartment = !!getId?.department;
      setTravelType(getId?.travelType || "Allowance");
      setKilometers(getId?.kilometer || "");
      setRatePerKm(hasDepartment ? "" : (getId?.ratePerKm != null ? String(getId?.ratePerKm) : ""));
      setTaxablePercentage(hasDepartment ? "" : (getId?.taxablePercentage || ""));
      setFromDate(getId?.start ? dayjs(getId.start) : null);

      setToDate(getId?.end ? dayjs(getId.end) : null);
      setDescription(getId?.description || "");
      setDepartment(getId?.department || "");
      setSelectedOptions(getId?.employeeDetails || []);
      setAllowanceStatus(getId?.status || null);
      if (!hasDepartment && typeof getSingleTravelClaimData === "function" && getId?.employeeDetails?.length) {
        getSingleTravelClaimData(getId?.employeeDetails?.[0]?._id);
      }
      // Store original data for comparison
      setOriginalData({
        kilometer: getId?.kilometer || "",
        ratePerKm: hasDepartment ? "" : (getId?.ratePerKm != null ? String(getId?.ratePerKm) : ""),
        taxablePercentage: getId?.taxablePercentage || "",
        start: getId?.start ? dayjs(getId.start) : null,
        end: getId?.end ? dayjs(getId.end) : null,
        description: getId?.description || "",
        department: getId?.department || "",
        employeeDetails: getId?.employeeDetails || [],
        travelType: getId?.travelType || "Allowance"
      });
      defaultsRequestKeyRef.current = "";
    } else {
      setTravelType("Allowance");
      setKilometers("");
      setRatePerKm("");
      setTaxablePercentage("");
      setFromDate(dayjs());
      setToDate(dayjs());
      setDescription("");
      setDepartment("");
      setDepartmentId("");
      setSelectedOptions([]);
      setOriginalData(null);
      setAllowanceStatus(null);
      setRatePerKm("");
      setTaxablePercentage("");
      setEmployeeEmailSelected(false);
      defaultsRequestKeyRef.current = "";
    }
  }, [isEdit, getId]);

  const handleEdit = (type, id) => {
    setIsEdit(type);
    setGetId(id);
    setOpen(true); // Open modal when editing
  };

  const isApprovedEdit = isEdit === "edit" && allowanceStatus === "Approved";
  const isEmailDisabled = Boolean(department) || isApprovedEdit;
  const modalActionDisabled = isEdit === "edit"
    ? isApprovedEdit || isApplyButtonDisabled()
    : isApplyButtonDisabled();


  const handleChange = (event, newValue) => {
    if (isApprovedEdit) return;
    const valuesArray = Array.isArray(newValue) ? newValue : newValue ? [newValue] : [];
    const filtered = valuesArray.filter((option) => option?._id);

    setSelectedOptions(filtered);
    setEmployeeEmailSelected(filtered.length > 0);
    defaultsRequestKeyRef.current = "";
    if (filtered.length > 0) {
      setDepartment("");
      setRatePerKm("");
      setTaxablePercentage("");
      if (typeof getSingleTravelClaimData === "function") {
        getSingleTravelClaimData(filtered[0]?._id);
      }
    } else {
      setRatePerKm("");
      setTaxablePercentage("");
      if (typeof getSingleTravelClaimData === "function") {
        getSingleTravelClaimData(null);
      }
    }
  };

  const handleDepartmentChange = (e) => {
    if (isApprovedEdit) return;
    const selectedDept = e.target.value;
      setDepartment(selectedDept);
    if (selectedDept) {
      setEmployeeEmailSelected(false);
    }
    defaultsRequestKeyRef.current = "";

    if (selectedDept) {
      DepartmentPayroll(selectedDept);
      const employeesForDept =
        selectedDept === "All"
          ? empData
          : empData.filter((emp) => emp.personalInfo.department === selectedDept);
      setSelectedOptions(employeesForDept || []);
      setRatePerKm("");
      setTaxablePercentage("");
    } else {
      setSelectedOptions([]);
      setRatePerKm("");
      setTaxablePercentage("");
    }
  };

  useEffect(() => {
    if (!department) {
      setDepartmentId("");
      return;
    }
    if (department === "All") {
      setDepartmentId("");
      return;
    }
    const matched = departmentData?.find((item) => item?.name === department);
    setDepartmentId(matched?._id || "");
  }, [department, departmentData]);

  useEffect(() => {
    if (isApprovedEdit) return;

    if (!department) {
      if (!employeeEmailSelected) {
        setRatePerKm("");
        setTaxablePercentage("");
      }
      defaultsRequestKeyRef.current = "";
      return;
    }

    if (employeeEmailSelected) {
      return;
    }

    if (travelType !== "Allowance") {
      setRatePerKm("");
      setTaxablePercentage("");
      defaultsRequestKeyRef.current = `${departmentId || department}-Reimbursement`;
      return;
    }

    const departmentKey = departmentId || department;
    const requestKey = `${departmentKey}-Allowance`;
    if (defaultsRequestKeyRef.current === requestKey) {
      return;
    }

    defaultsRequestKeyRef.current = requestKey;
    setRatePerKm("");
    setTaxablePercentage("");
    let isCancelled = false;

    const fetchDefaults = async () => {
      if (typeof fetchTravelAllowanceDefaults !== "function") return;
      try {
        const response = await fetchTravelAllowanceDefaults({
          departmentId: departmentId || undefined,
          department: departmentKey,
        });
        if (isCancelled || defaultsRequestKeyRef.current !== requestKey) {
          return;
        }
        const taxableFromApi =
          response?.taxablePercentage ??
          response?.taxable ??
          response?.taxableValue ??
          response?.taxRate;
        setTaxablePercentage(
          taxableFromApi !== undefined && taxableFromApi !== null ? String(taxableFromApi) : ""
        );
      } catch (error) {
        if (!isCancelled && defaultsRequestKeyRef.current === requestKey) {
          setTaxablePercentage("");
        }
      }
    };

    fetchDefaults();

    return () => {
      isCancelled = true;
    };
  }, [department, departmentId, travelType, employeeEmailSelected, fetchTravelAllowanceDefaults, isApprovedEdit]);

  useEffect(() => {
    if (isApprovedEdit) return;
    if (!employeeEmailSelected || !selectedOptions?.length) {
      return;
    }

    const employeeId = selectedOptions[0]?._id;
    if (!employeeId || typeof fetchTravelAllowanceDefaults !== "function") {
      return;
    }

    const requestKey = `employee-${employeeId}`;
    if (defaultsRequestKeyRef.current === requestKey) {
      return;
    }

    defaultsRequestKeyRef.current = requestKey;
    setRatePerKm("");
    setTaxablePercentage("");
    let isCancelled = false;

    const fetchDefaultsByEmployee = async () => {
      try {
        const response = await fetchTravelAllowanceDefaults({ employeeId });
        if (isCancelled || defaultsRequestKeyRef.current !== requestKey) {
          return;
        }
        const rateValue =
          response?.ratePerKm ??
          response?.rate_per_km ??
          response?.rate ??
          response?.kilometerRate;
        const taxableValue =
          response?.taxablePercentage ??
          response?.taxable ??
          response?.taxableValue ??
          response?.taxRate;
        setRatePerKm(rateValue != null ? String(rateValue) : "");
        setTaxablePercentage(
          travelType === "Allowance" && taxableValue != null ? String(taxableValue) : ""
        );
      } catch (error) {
        if (!isCancelled && defaultsRequestKeyRef.current === requestKey) {
          setRatePerKm("");
          setTaxablePercentage("");
        }
      }
    };

    fetchDefaultsByEmployee();

    return () => {
      isCancelled = true;
    };
  }, [employeeEmailSelected, selectedOptions, fetchTravelAllowanceDefaults, isApprovedEdit, travelType]);

  useEffect(() => {
    if (isApprovedEdit) return;
    if (employeeEmailSelected && travelType !== "Allowance") {
      setTaxablePercentage("");
    }
  }, [employeeEmailSelected, travelType, isApprovedEdit]);

  useEffect(() => {
    setEmpDataId(employeeData?.filter(emp => !selectedOptions?.some(sel => sel._id === emp._id)) || []);
  }, [employeeData, selectedOptions]);

  return (
    <>
      <Box
        px="15px"
        pt="20px"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography sx={styles.leaveHeading}>{isEmployeeRole ? "My Travel Claims Management" : "Travel Claims Management"}</Typography>
      </Box>
      <Box sx={{ backgroundColor: theme.palette.white.main, m: 2, borderRadius: "20px" }}>
        <SubHeader
          title={isEmployeeRole ? "My Travel Claims Requests List" : "Travel Claims Requests List"}
          btn={permission?.travelAllowance?.isEdit || role === "Admin" || role === "SuperAdmin"}
          btn2={permission?.travelAllowance?.isEdit || role === "Admin" || role === "SuperAdmin"}
          search={permission?.travelAllowance?.isView || role === "Admin" || role === "SuperAdmin"}
          sortBy={permission?.travelAllowance?.isView || role === "Admin" || role === "SuperAdmin"}
          BtnText={"Add Request"}
          BtnText2={"Approve All"}
          onClick={() => {
            setOpen(true);
            setIsEdit("Add");
            setGetId(null);
            setDepartment("");
            setSelectedOptions([]);
            setRatePerKm("");
            setTaxablePercentage("");
            setEmployeeEmailSelected(false);
            defaultsRequestKeyRef.current = "";
          }}
          onClick2={() => getApprovedTravelAllowance()}
          onchange={handleFilter}
          handleSort={handleSort}
          onCompanyChange={getLeaveManagementLists}
        />
        <TravelallowancesList
          data={data}
          setPage={setPage}
          page={page}
          total={total}
          permission={permission?.travelAllowance?.isEdit || role === "Admin" || role === "SuperAdmin"}
          setDeleteOpen={setDeleteOpen}
          setOpen={setOpen}
          deleteOpen={deleteOpen}
          handleEdit={handleEdit}
          DeleteCommission={DeleteCommission}
        />
        <Modal
          open={open}
          setOpen={setOpen}
          btnText={isEdit === "edit" ? "Update" : "Apply"}
          disabled={modalActionDisabled}
          title={"Travel Claims Request"}
          approveTxt={isEdit === "edit"}
          handleRequest={(res) => handleRequest(getId._id, res)}
          handleCheck={() => {
            const payload = {
              start: dayjs(fromDate).format("YYYY-MM-DD"),
              end: dayjs(toDate).format("YYYY-MM-DD"),
              kilometer: Number(kilometers),
              desc: description,
              travelType,
              ratePerKm: department ? 0 : Number(ratePerKm || 0),
              taxablePercentage:
                travelType === "Allowance" ? Number(taxablePercentage || 0) : 0,
              department: department || "",
            };

            if (isEdit === "edit") {
              updateTravelAllowances(getId?._id, payload);
            } else {
              const employeesPayload = {
                ...payload,
                employees: selectedOptions.map((option) => option._id),
              };
              handleLeaveApply(employeesPayload);
            }

            setTravelType("Allowance");
            setKilometers("");
            setRatePerKm("");
            setTaxablePercentage("");
            setFromDate(dayjs());
            setToDate(dayjs());
            setDescription("");
            setDepartment("");
            setSelectedOptions([]);
            setEmployeeEmailSelected(false);
            defaultsRequestKeyRef.current = "";
            setOpen(false);
          }}
          loading={loading}
          onClose={() => {
            setTravelType("Allowance");
            setKilometers("");
            setRatePerKm("");
            setTaxablePercentage("");
            setFromDate(dayjs());
            setToDate(dayjs());
            setDepartment("");
            setSelectedOptions([]);
            setIsEdit(null);
            setGetId(null);
            setOriginalData(null);
            setEmployeeEmailSelected(false);
            defaultsRequestKeyRef.current = "";
          }}
          status={allowanceStatus}
        >
          <Box sx={styles.containerInput}>
            <Box sx={styles.dateGrid}>
              <Box sx={{ width: "50%", mr: 1 }}>
                <InputLabel sx={styles.label}>Travel Type</InputLabel>
                <Select
                  value={travelType}
                  onChange={(e) => setTravelType(e.target.value)}
                  sx={{ ...styles.modalInput, borderRadius: "8px", my: "10px" }}
                  displayEmpty
                  disabled={isApprovedEdit}
                >
                  <MenuItem value="Allowance">Allowance</MenuItem>
                  <MenuItem value="Reimbursement">Reimbursement</MenuItem>
                </Select>
              </Box>
              <Box sx={{ mr: 1, width: "50%" }}>
                <Autocomplete
                  id="autocomplete"
                  options={empDataId || []}
                  getOptionLabel={(option) => option?.personalInfo?.email}
                  disabled={isEmailDisabled}
                  onInputChange={(event, newInputValue) => {
                    handleEmployee(newInputValue);
                  }}
                  onChange={handleChange}
                  renderInput={(params) => (
                    <>
                      <InputLabel sx={styles.label}>Employee Email</InputLabel>
                      <TextField
                        {...params}
                        sx={{
                          ...styles.modalInput,
                          ...styles.inputSpace,
                          "& input::placeholder": {
                            color: isEmailDisabled ? "#6b7280" : "black",
                            opacity: 1,
                          },
                          "& .MuiInputBase-root.Mui-disabled": {
                            backgroundColor: "#ffffff",
                            color: "#6b7280",
                          },
                          "& .MuiInputLabel-root.Mui-disabled": {
                            color: "#9ca3af",
                          },
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "#6b7280",
                          },
                        }}
                        placeholder={getId?.employeeDetails?.[0]?.personalInfo?.email || "Select Employee"}
                        variant="outlined"
                        disabled={isEmailDisabled}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    </>
                  )}
                />
              </Box>
            </Box>
            <Box sx={styles.dateGrid}>

              <Box sx={{ width: "50%", mt: 1, mr: 1 }}>
                <InputLabel sx={styles.label}>Department</InputLabel>
                <Select
                  value={department}
                  onChange={handleDepartmentChange}
                  sx={{ ...styles.modalInput, borderRadius: "8px", my: "10px" }}
                  disabled={employeeEmailSelected || isApprovedEdit}
                  displayEmpty
                  renderValue={(selected) =>
                    selected ? selected : "Select Department"
                  }
                >
                  <MenuItem disabled value="">
                    Select
                  </MenuItem>
                  <MenuItem value="All">All</MenuItem>
                  {departmentData?.map((item, index) => (
                    <MenuItem
                      key={index}
                      value={item?.name}
                      onClick={() => DepartmentPayroll(item?.name)}
                    >
                      {item?.name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Box sx={{ width: "50%", mt: 1, mr: 1 }}>
                <InputLabel sx={styles.label}>Kilometers</InputLabel>
                <TextField
                  value={kilometers}
                  name="kilometers"
                  placeholder="Enter Kilometers"
                  type="number"
                  inputProps={{ min: "1", step: "1" }}
                  onChange={(e) => setKilometers(e.target.value)}
                  onKeyPress={(e) => {
                    if (["-", "e", "E", "+"].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  sx={styles.modalInputs}
                  disabled={isApprovedEdit}
                />
              </Box>
            </Box>
            <Box sx={styles.dateGrid}>
              <Box
                sx={{
                  width: travelType === "Allowance" ? "50%" : "100%",
                  mt: 1,
                  mr: 1,
                }}
              >
                <InputLabel sx={styles.label}>Rate per Km</InputLabel>
                <TextField
                  value={ratePerKm}
                  name="ratePerKm"
                  placeholder="Enter Rate per Km"
                  type="number"
                  sx={styles.modalInputs}
                  disabled
                />
              </Box>
              {travelType === "Allowance" && (
                <Box sx={{ width: "50%", mt: 1, mr: 1 }}>
                  <InputLabel sx={styles.label}>Taxable %</InputLabel>
                  <TextField
                    value={taxablePercentage ?? ""}
                    name="taxablePercentage"
                    placeholder="Enter Taxable Percentage"
                    type="number"
                    sx={styles.modalInputs}
                    disabled
                  />
                </Box>
              )}
            </Box>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer
                components={["DatePicker", "DatePicker"]}
                sx={styles.calender}
              >
                <Box>
                  <InputLabel sx={styles.label}>Start</InputLabel>
                  <DatePicker
                    value={fromDate}
                    onChange={(newValue) => setFromDate(newValue)}
                    sx={styles.calenderInput}
                    slotProps={{
                      layout: { sx: { marginTop: -30 } },
                      field: { readOnly: true },
                    }}
                    disabled={isApprovedEdit}
                  />
                </Box>
                <Box>
                  <InputLabel sx={styles.label}>End</InputLabel>
                  <DatePicker
                    value={toDate}
                    onChange={(newValue) => setToDate(newValue)}
                    sx={styles.calenderInput}
                    slotProps={{
                      layout: { sx: { marginTop: -30 } },
                      field: { readOnly: true },
                    }}
                    disabled={isApprovedEdit}
                    minDate={fromDate}
                  />
                </Box>
              </DemoContainer>
            </LocalizationProvider>
            <Box sx={{ my: 2 }}>
              <InputLabel sx={styles.label}>Description</InputLabel>
              <TextField
                multiline
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                sx={styles.reason}
                disabled={isApprovedEdit}
              />
            </Box>
          </Box>
        </Modal>
      </Box>
    </>
  );
};

export default TravelallowancesPage;
